import React, { useState } from 'react';
import { submitBwaOnlyUpload } from '../services/neukundenIntakeService';

const fieldClass =
  'w-full max-w-full rounded-lg bg-[#0F172A] border border-slate-600 px-3 py-3 text-base text-[#F8FAFC] placeholder:text-slate-500 focus:outline-none focus:border-[#DCB68A]';

async function looksLikePdf(file: File): Promise<boolean> {
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  if (name.endsWith('.pdf') || type.includes('pdf')) return true;
  try {
    const head = new TextDecoder().decode(await file.slice(0, 5).arrayBuffer());
    return head.startsWith('%PDF');
  } catch {
    return false;
  }
}

const BwaUploadPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kundennummer, setKundennummer] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onFile = (list: FileList | null) => {
    const next = list?.[0];
    if (!next) return;
    void looksLikePdf(next).then((ok) => {
      if (!ok) {
        setFile(null);
        setError('Bitte eine PDF wählen.');
        return;
      }
      setError('');
      setFile(next);
    });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name fehlt.');
      return;
    }
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('E-Mail ungültig.');
      return;
    }
    if (!file) {
      setError('BWA-PDF fehlt.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await submitBwaOnlyUpload({ name, email, kundennummer, file });
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Hochladen fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F8FAFC] flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-[#1E293B] border border-[#DCB68A]/40 rounded-2xl p-8 text-center">
          <p className="text-[#DCB68A] text-xs font-black uppercase tracking-widest mb-3">Eingegangen</p>
          <h1 className="text-2xl font-black mb-4">Danke, {name.trim()}.</h1>
          <p className="text-slate-300">Deine BWA ist angekommen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#0A0A0A] text-[#F8FAFC]">
      <div className="max-w-xl mx-auto px-4 py-6 sm:py-10">
        <p className="text-[#DCB68A] text-xs font-black uppercase tracking-[0.25em] mb-2">PreisExpert</p>
        <h1 className="text-3xl font-black mb-2">BWA-Upload</h1>
        <p className="text-slate-400 mb-8">Name und PDF. E-Mail und Mandant-Nummer nur, wenn du sie schon hast.</p>
        <form onSubmit={onSubmit} className="bg-[#1E293B] border border-slate-700 rounded-2xl p-6 space-y-4">
          <label className="block text-xs uppercase tracking-wider text-slate-400">Name *
            <input className={`${fieldClass} mt-1`} value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block text-xs uppercase tracking-wider text-slate-400">E-Mail
            <input className={`${fieldClass} mt-1`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="block text-xs uppercase tracking-wider text-slate-400">Mandant-Nummer
            <input className={`${fieldClass} mt-1`} value={kundennummer} onChange={(e) => setKundennummer(e.target.value)} />
          </label>
          <label className="block text-xs uppercase tracking-wider text-slate-400">BWA als PDF *
            <input
              className="block mt-2 w-full max-w-full text-sm file:mr-3 file:mb-2 file:rounded-lg file:border-0 file:bg-[#DCB68A] file:px-3 file:py-2 file:font-bold file:text-black"
              type="file"
              accept=".pdf,application/pdf,application/x-pdf"
              onChange={(e) => { onFile(e.target.files); e.target.value = ''; }}
            />
          </label>
          {file && <p className="text-sm text-[#DCB68A]">{file.name}</p>}
          {error && <p className="text-red-400 text-sm font-bold">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full py-4 px-4 rounded-xl bg-[#DCB68A] text-black text-sm font-black uppercase tracking-wide disabled:opacity-60"
          >
            {busy ? 'Wird hochgeladen…' : 'BWA hochladen'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BwaUploadPage;
